import {
	Component,
	OnDestroy,
	OnInit,
	ChangeDetectionStrategy,
} from '@angular/core';
import {
	MatButtonToggleChange,
	MatButtonToggleGroup,
	MatButtonToggle,
} from '@angular/material/button-toggle';
import { PageEvent } from '@angular/material/paginator';
import { Router } from '@angular/router';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { LegacyApiService } from '@lmc-app/core/services/legacy-api.service';
import { CellClickedEvent } from 'ag-grid-community';
import { ApplicationService } from '@lmc-app/core/services/application.service';
import { Store } from '@ngrx/store';
import { selectUser, State } from '@lmc-app/store';
import { ModuleFieldPicklist } from '@lmc-app/shared/interface/module-field-picklist';
import { buildApplicationsFormConfig } from './applications-form.config';
import { User } from '@lmc-app/shared/interface/user.interface';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { ApplicationsFormOptions as ApplicationsFormOptions } from './applications-form-options.interface';
import { MatIcon } from '@angular/material/icon';
import { MatListItemIcon } from '@angular/material/list';
import { NgIf } from '@angular/common';
import { StateDisplayComponent } from '../shared/components/state-display/state-display.component';
import { CustomTableComponent } from '../custom-table/custom-table.component';

@Component({
	selector: 'app-applications',
	templateUrl: './applications.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./applications.component.scss'],
	imports: [
		MatButtonToggleGroup,
		MatButtonToggle,
		ReactiveFormsModule,
		FormlyModule,
		MatIcon,
		MatListItemIcon,
		NgIf,
		StateDisplayComponent,
		CustomTableComponent,
	],
})
export class ApplicationsComponent implements OnInit, OnDestroy {
	// Formly configuration
	protected form = new FormGroup({});
	protected model: { keyword: string; stage: string } = {
		keyword: '',
		stage: 'All',
	};
	protected fields: FormlyFieldConfig[] = [];

	// Dropdown options as Observables
	private stages$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);

	columns = [
		{
			headerName: 'Created time',
			field: 'Created_Time',
			sortable: true,
			filter: 'agTextColumnFilter',
			autoHeight: true,
		},
		{
			headerName: 'Application name',
			field: 'Deal_Name',
			sortable: true,
			filter: 'agTextColumnFilter',
			autoHeight: true,
		},
		{
			headerName: 'Stage',
			field: 'Stage',
			sortable: true,
			filter: 'agTextColumnFilter',
			autoHeight: true,
		},
		{
			headerName: 'Agency',
			field: 'Account_Name.name',
			sortable: true,
			filter: 'agTextColumnFilter',
			autoHeight: true,
		},
		{
			headerName: 'Referral date',
			field: 'Referral_Date',
			sortable: true,
			filter: 'agTextColumnFilter',
			autoHeight: true,
		},
		{
			headerName: 'Collector',
			field: 'Referred_By.name',
			sortable: true,
			filter: 'agTextColumnFilter',
			autoHeight: true,
		},
		{
			headerName: 'Amount',
			field: 'Amount',
			sortable: true,
			filter: 'agTextColumnFilter',
			autoHeight: true,
		},
		{
			headerName: 'Most recent note',
			field: 'Reason_for_Poor_credit_score',
			sortable: false,
			filter: 'agTextColumnFilter',
			autoHeight: true,
		},
		{
			headerName: 'Owner',
			field: 'Owner.name',
			sortable: false,
			filter: 'agTextColumnFilter',
			autoHeight: true,
		},
		{
			headerName: 'API created by',
			field: 'API_Created_By.name',
			sortable: false,
			filter: 'agTextColumnFilter',
			autoHeight: true,
		},
	];
	public dropdownFilters = [
		{
			label: 'Stage',
			filterColumn: 'Stage',
			filters: [],
		},
	];
	currentUser: User | null = null;
	applications: Application[] = [];
	zohoOwnerId: string = '';
	stages: string[] | null = null;
	availableStages: string[] | null = null;
	page: number = 1;
	perPage: number = 200;
	filter: string = 'all';
	// Loading states
	isLoading: boolean = false;
	isLoadingStages: boolean = false;
	isLoadingEmployees: boolean = false;
	count: number = 0;
	hasMoreRecord: boolean = false;
	private readonly _onDestroy = new Subject<void>();

	constructor(
		private readonly apiService: LegacyApiService,
		private readonly applicationService: ApplicationService,
		private readonly router: Router,
		private readonly store: Store<State>
	) {}

	ngOnInit(): void {
		this.isLoading = true;
		this.initializeFormConfig();
		this.getStages();

		this.store
			.select(selectUser)
			.pipe(takeUntil(this._onDestroy))
			.subscribe((user: User | null) => {
				this.currentUser = user;
				if (this.currentUser) {
					this.getApplications();
				}
			});
	}

	ngOnDestroy(): void {
		this._onDestroy.next();
		this._onDestroy.complete();
	}

	private getStages() {
		this.availableStages = [
			'All',
			'Incomplete Application',
			'AOB',
			'Needs Documents',
			'Needs Co-App',
			'Co-App Obtained',
			'Rapid - Incomplete Application',
		];
		if (this.filter === 'open') {
			this.availableStages = [
				'All',
				'Incomplete Application',
				'Rapid - Incomplete Application',
				'Needs Documents',
				'Needs Co-App',
				'Co-App Obtained',
			];
		}

		if (!this.availableStages.includes(this.model.stage)) {
			this.model = {
				...this.model,
				stage: 'All',
			};
		}

		this.stages$.next(
			this.availableStages.map((stage) => ({
				display_value: stage,
			}))
		);
		this.checkLoadingComplete();
	}

	private initializeFormConfig() {
		const options: ApplicationsFormOptions = {
			stages$: this.stages$,
			onKeywordChanged: () => this.onKeywordChanged(),
			onStageChanged: () => this.onStageChanged(),
		};
		this.fields = buildApplicationsFormConfig(options);
	}

	onKeywordChanged() {
		this.page = 1;
		this.getApplications();
	}

	onStageChanged() {
		this.page = 1;
		this.getApplications();
	}

	getApplications() {
		this.isLoading = true;
		if (this.model.stage === 'All') {
			this.stages = this.availableStages;
		} else {
			this.stages = [this.model.stage];
		}
		this.applicationService
			.getApplications(
				this.zohoOwnerId,
				this.model.keyword,
				this.stages,
				this.page,
				this.perPage
			)
			.subscribe({
				next: (res) => {
					this.applications = res.data.map((app) => ({
						...app,
						Created_Time: app.Created_Time.replace('T', ' ').substring(0, 19),
					}));
					this.count = res.info.count;
					this.hasMoreRecord = res.info.more_records;
					this.isLoading = false;
				},
				error: (err) => {
					console.error(err);
					this.isLoading = false;
				},
			});
	}

	onPageChange(event: PageEvent): void {
		this.page = event.pageIndex + 1;
		this.getApplications();
	}

	onPrevPage(): void {
		if (this.page === 1 || this.isLoading) return;

		this.page--;
		this.getApplications();
	}

	onNextPage(): void {
		if (!this.hasMoreRecord || this.isLoading) return;

		this.page++;
		this.getApplications();
	}

	onCellClicked(e: CellClickedEvent) {
		const url = this.router.serializeUrl(
			this.router.createUrlTree([`applications/summary/${e.data.id}`])
		);
		window.open(url, '_blank');
	}

	onFilterChange(event: MatButtonToggleChange) {
		this.zohoOwnerId = '';
		this.page = 1;
		this.filter = event.value;
		this.getStages();
		switch (event.value) {
			case 'my':
				this.zohoOwnerId = this.currentUser.zoho_owner_id;
				break;
			default:
				break;
		}
		this.getApplications();
	}

	private checkLoadingComplete(): void {
		// Check if all initial data loading is complete
		if (!this.isLoadingStages && !this.isLoadingEmployees && !this.isLoading) {
			// All loading complete
		}
	}
}
